// Catálogos de configuração servidos pelo backend (ADR-0011).
//
// Estados, profissões, planos, permissões, categorias — tudo que é conjunto
// fechado vive no banco e chega pela função `config`, que responde do Redis.
// Aqui fica a segunda camada de cache, a da sessão: uma tela que abre e fecha
// dez vezes faz uma requisição, não dez.
//
// O cache guarda **configuração**, nunca dado de Tenant. A função `config` só
// serve catálogos de uma allowlist justamente para que essa promessa se
// sustente: não há como pedir a ela a tabela de condomínios.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { UnidadeDePeriodo } from '@/lib/periodo';

/** Chaves aceitas pela função `config`. */
export type ChaveDeCatalogo =
  | 'planos'
  | 'profissoes'
  | 'estados'
  | 'alvaras_templates'
  | 'papeis'
  | 'permissoes'
  | 'categorias_servico'
  | 'categorias_alvara'
  | 'treinamentos'
  | 'notificacao_parametros'
  | 'notificacao_canais'
  | 'manutencao_tipos'
  | 'infraestrutura_categorias'
  | 'infraestrutura_itens';

export interface Estado {
  uf: string;
  nome: string;
}

export interface CategoriaServico {
  id: string;
  nome: string;
  icone: string | null;
  cor: string;
  ordem: number;
}

/**
 * Treinamento do catálogo, em `snake_case` como o banco entrega.
 *
 * `profissao_id` saiu na migration 0047: o destinatário virou N:N, e a função
 * `config` devolve a lista em `profissoes`. `vigencia_dias` continua, agora
 * derivada do Período pelo banco — é só leitura.
 */
export interface TreinamentoCatalogo {
  id: string;
  nome: string;
  norma: string | null;
  url_legislacao: string | null;
  abrangencia: string;
  vigencia_valor: number;
  vigencia_unidade: UnidadeDePeriodo;
  vigencia_dias: number;
  prazo_valor: number | null;
  prazo_unidade: UnidadeDePeriodo | null;
  carga_horaria: number | null;
  obrigatorio: boolean;
  descricao: string | null;
  aplica_a_qualquer_profissao: boolean;
  dias_antecedencia_aviso: number | null;
  tempo_medio_geracao_valor: number | null;
  tempo_medio_geracao_unidade: UnidadeDePeriodo | null;
  profissoes: string[];
}

/** Categoria de item de infraestrutura (migration 0046). */
export interface CategoriaInfraestrutura {
  id: string;
  nome: string;
  icone: string | null;
  cor: string;
  ordem: number;
}

/** Definição de um campo extra do item de catálogo. */
export interface CampoExtra {
  chave: string;
  rotulo: string;
  tipo:
    | 'texto'
    | 'textarea'
    | 'numero'
    | 'data'
    | 'booleano'
    | 'select'
    | 'multiselect'
    | 'cpf'
    | 'cnpj'
    | 'cep'
    | 'telefone'
    | 'arquivo';
  obrigatorio?: boolean;
  opcoes?: { valor: string; rotulo: string }[];
}

/**
 * Item de infraestrutura do catálogo global (secao 4.5).
 *
 * `treinamentos` e `alvaras` viajam junto porque são as duas perguntas que a
 * tela faz assim que alguém escolhe um item — o que a operação precisa saber
 * fazer, e que licença o item obriga.
 */
export interface ItemDeInfraestrutura {
  id: string;
  nome: string;
  descricao: string;
  categoria_id: string;
  campos_extras: CampoExtra[];
  periodicidade_manutencao_valor: number | null;
  periodicidade_manutencao_unidade: UnidadeDePeriodo | null;
  periodicidade_manutencao_dias: number | null;
  treinamentos: string[];
  alvaras: string[];
}

/** Tipo de manutenção (migration 0031). Mesma forma de `CategoriaServico`. */
export interface TipoDeManutencao {
  id: string;
  nome: string;
  icone: string | null;
  cor: string;
  ordem: number;
}

export interface CategoriaAlvara {
  id: string;
  nome: string;
}

/**
 * Janela de antecedência de um tipo de alerta (migration 0028).
 *
 * `dias_minimo`/`dias_maximo` vêm do banco em vez de estarem no `min`/`max` do
 * controle: a faixa aceita é configuração, e a constraint que a garante mora
 * junto do valor. `aplicado` diz se já existe gatilho consumindo o parâmetro —
 * a tela mostra isso em vez de prometer efeito que ainda não existe.
 */
export interface ParametroNotificacao {
  chave: string;
  rotulo: string;
  descricao: string | null;
  dias: number;
  dias_minimo: number;
  dias_maximo: number;
  aplicado: boolean;
  ordem: number;
}

export interface CanalNotificacao {
  chave: string;
  rotulo: string;
  descricao: string | null;
  ativo: boolean;
  aplicado: boolean;
  ordem: number;
}

const cache = new Map<string, unknown[]>();
const emVoo = new Map<string, Promise<unknown[]>>();

async function buscar(chave: ChaveDeCatalogo): Promise<unknown[]> {
  const guardado = cache.get(chave);
  if (guardado) return guardado;

  // Duas telas montando ao mesmo tempo pedem o mesmo catálogo: sem isto, as
  // duas disparariam a requisição antes de qualquer uma preencher o cache.
  const jaPedido = emVoo.get(chave);
  if (jaPedido) return jaPedido;

  const promessa = supabase.functions
    .invoke(`config?chave=${chave}`, { method: 'GET' })
    .then(({ data, error }) => {
      if (error) throw new Error(error.message);
      const itens = Array.isArray(data) ? data : [];
      cache.set(chave, itens);
      return itens;
    })
    .finally(() => {
      emVoo.delete(chave);
    });

  emVoo.set(chave, promessa);
  return promessa;
}

/**
 * Invalida o catálogo em memória.
 *
 * Chame depois de alterar a configuração pela aplicação — o backend tem a
 * função `refresh-cache` para o lado dele, e esta é a contraparte na sessão.
 * Sem chave, esvazia tudo.
 */
export function invalidarCatalogo(chave?: ChaveDeCatalogo): void {
  if (chave) cache.delete(chave);
  else cache.clear();
}

/**
 * Lê um catálogo de configuração.
 *
 * Falha de rede devolve lista vazia com `erro` preenchido: um select sem opções
 * é ruim, mas travar a tela inteira por causa de um catálogo é pior. Quem
 * precisa de comportamento diferente decide a partir de `erro`.
 */
export function useCatalogo<T>(chave: ChaveDeCatalogo): {
  itens: T[];
  carregando: boolean;
  erro: string | null;
  /** Descarta o cache e busca de novo — use depois de alterar a configuração. */
  recarregar: () => void;
} {
  const [itens, setItens] = useState<T[]>(() => (cache.get(chave) as T[]) ?? []);
  const [carregando, setCarregando] = useState(!cache.has(chave));
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    let cancelado = false;

    const guardado = cache.get(chave) as T[] | undefined;
    if (guardado) {
      setItens(guardado);
      setCarregando(false);
      return;
    }

    setCarregando(true);
    buscar(chave)
      .then((dados) => {
        if (cancelado) return;
        setItens(dados as T[]);
        setErro(null);
      })
      .catch((e: unknown) => {
        if (cancelado) return;
        setErro(e instanceof Error ? e.message : 'Falha ao carregar a configuração.');
        setItens([]);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [chave, versao]);

  const recarregar = useCallback(() => {
    invalidarCatalogo(chave);
    setVersao((v) => v + 1);
  }, [chave]);

  return { itens, carregando, erro, recarregar };
}

/**
 * Classes da categoria a partir do token de cor.
 *
 * O banco guarda `azul`; a escolha de como pintar é da interface. Trocar o
 * design system muda este mapa, não os dados.
 */
export const CLASSES_POR_COR: Record<string, string> = {
  azul: 'bg-blue-100 text-blue-900 border-blue-200',
  verde: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  amarelo: 'bg-amber-100 text-amber-900 border-amber-200',
  vermelho: 'bg-red-100 text-red-900 border-red-200',
  roxo: 'bg-violet-100 text-violet-900 border-violet-200',
  ciano: 'bg-cyan-100 text-cyan-900 border-cyan-200',
  neutro: 'bg-background-100 text-foreground-700 border-background-300',
};

export const classesDaCategoria = (cor: string | undefined): string =>
  CLASSES_POR_COR[cor || 'neutro'] || CLASSES_POR_COR.neutro;
